// Admin store settings page at /admin/store — name, address, phone, receipt footer.
import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPut } from "@/lib/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface StoreInfo {
  name: string;
  address: string;
  phone: string;
  footer: string;
}

interface Workstation {
  id: string;
  name: string;
  warehouseName: string;
  isActive: boolean;
}

export function StoreSettingsPage() {
  const qc = useQueryClient();
  const [form, setForm] = useState<StoreInfo>({ name: "", address: "", phone: "", footer: "" });
  const [saved, setSaved] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["store-info"],
    queryFn: () => apiGet<StoreInfo>("/v1/settings/store-info"),
  });

  const { data: workstations = [] } = useQuery({
    queryKey: ["workstations"],
    queryFn: () => apiGet<Workstation[]>("/v1/workstations"),
  });

  // The chosen POS terminal for this browser. Stored as workstation id in
  // localStorage; the server maps it to the right warehouse + printer.
  const [workstationId, setWorkstationId] = useState(
    () => localStorage.getItem("pos_workstation_id") ?? "",
  );

  useEffect(() => {
    if (data) setForm(data);
  }, [data]);

  const mutation = useMutation({
    mutationFn: () => apiPut<StoreInfo>("/v1/settings/store-info", form),
    onSuccess: (updated) => {
      qc.setQueryData(["store-info"], updated);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    },
  });

  const set = (key: keyof StoreInfo) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((p) => ({ ...p, [key]: e.target.value }));

  if (isLoading) return <div className="p-6 text-muted-foreground">Đang tải...</div>;

  return (
    <div className="mx-auto max-w-lg space-y-6">
      <h1 className="text-xl font-semibold">Thông tin cửa hàng</h1>

      <div className="space-y-4 rounded-xl border bg-card p-6">
        <div className="space-y-1">
          <Label>Tên cửa hàng</Label>
          <Input value={form.name} onChange={set("name")} placeholder="Nhật Minh Store" />
        </div>
        <div className="space-y-1">
          <Label>Địa chỉ</Label>
          <Input value={form.address} onChange={set("address")} placeholder="123 đường X, TP Y" />
        </div>
        <div className="space-y-1">
          <Label>Số điện thoại</Label>
          <Input value={form.phone} onChange={set("phone")} placeholder="0901234567" />
        </div>
        <div className="space-y-1">
          <Label>Lời cảm ơn (in trên hóa đơn)</Label>
          <Input value={form.footer} onChange={set("footer")} placeholder="Cảm ơn quý khách!" />
        </div>

        <Button
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending}
          className="w-full"
        >
          {mutation.isPending ? "Đang lưu..." : saved ? "Đã lưu!" : "Lưu thông tin"}
        </Button>
      </div>

      {/* Workstation selector for this browser session */}
      <div className="rounded-xl border bg-card p-6 space-y-3">
        <h2 className="font-medium">Máy POS hiện tại (phiên này)</h2>
        <p className="text-sm text-muted-foreground">
          Chọn quầy POS của máy này. Bán hàng sẽ trừ tồn theo kho của quầy và in
          hóa đơn ra máy in của quầy. Lưu trong trình duyệt.
        </p>
        <select
          value={workstationId}
          onChange={(e) => {
            const val = e.target.value;
            setWorkstationId(val);
            if (val) localStorage.setItem("pos_workstation_id", val);
            else localStorage.removeItem("pos_workstation_id");
          }}
          className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
        >
          <option value="">— Chưa chọn quầy —</option>
          {workstations
            .filter((w) => w.isActive || w.id === workstationId)
            .map((w) => (
              <option key={w.id} value={w.id}>
                {w.name} — {w.warehouseName}
              </option>
            ))}
        </select>
      </div>
    </div>
  );
}
